import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import type { Project, ProjectWithProperties } from "@/features/projects/services/projectsAdapter";
import { useToast } from "@/shared/hooks/use-toast";
import { requireUserIdForDatabase } from "@/shared/lib/userIdUtils";
import { fetchAuthorizedPropertyRefIds } from "@/features/projects/services/projectsApi";
import { resolveClientId } from "@/shared/lib/resolveClientId";
import { isAdminUser } from "@/shared/lib/adminUids";

type DbProject = {
  id: string;
  name: string;
  description?: string | null;
  created_at: string;
  updated_at?: string;
  created_by: string;
  status?: string | null;
  client_code?: string | null;
  logo_url?: string | null;
};

type DbProperty = {
  id: string;
  ref_id?: string | null;
  name: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  property_type?: string | null;
  created_at?: string;
  updated_at?: string | null;
  project_id?: string | null;
  organization_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  delivery_status?: string | null;
};

// Use an untyped alias to avoid TS errors until Supabase types are regenerated to include "projects" and "properties.project_id"
const db = supabase;

// Chunk large id arrays to keep Supabase IN() queries within URL length limits.
const CHUNK_SIZE = 200;
const chunkIds = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Stable empty references for the pre-load state, so the derived useMemos below
// don't recompute on every render while the query is pending.
const EMPTY_PROPERTIES: DbProperty[] = [];
const EMPTY_PROJECTS: DbProject[] = [];
const EMPTY_COUNTS: Record<string, number> = {};

export function useProjects() {
  const { user } = useAuth();
  const { toast } = useToast();
  const seededRef = useRef(false);

  // Portfolio + per-project asset counts. Shared by 8+ screens (sidebar, client
  // list, properties, assets, survey editor), so it lives in the query cache: one
  // fetch is de-duped across every concurrent caller and reused across navigations
  // until it goes stale, instead of each mount re-downloading the whole inventory.
  const {
    data,
    isPending,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["projects", user?.id ?? null] as const,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const supabaseUserId = requireUserIdForDatabase();
      // The 3 hardcoded admin UIDs (site-surveys workflow) see ALL orgs + properties.
      const admin = isAdminUser(supabaseUserId);

      // NOTE: team_id, delivery_date, site_visit_date columns don't exist on prod
      // (schema drift); kept out of the select. Optional fields degrade to undefined.
      // property_assets(count) is a server-side aggregate — one tally per property
      // instead of downloading every asset row just to count them client-side.
      const PROPERTY_SELECT =
        "id, ref_id, name, address, city, state, country, property_type, created_at, updated_at, project_id, organization_id, latitude, longitude, delivery_status, property_assets(count)";

      type PropertyRow = DbProperty & { property_assets?: { count: number }[] };

      const allProperties: PropertyRow[] = [];
      let allProjects: DbProject[] = [];
      // Single client org from the claim — only for non-admin (client-portal) users.
      let resolvedClientOrgId: string | null = null;

      if (admin) {
        // ── Admin all-access path (restored "as they were") ──────────────────
        // Every property + every project (admins bypass org scoping). Grouping
        // uses each row's own organization_id/project_id (multi-org).
        const pageSize = 1000;
        let from = 0;
        let hasMore = true;
        while (hasMore) {
          const { data, error: propsError } = await db
            .from("properties")
            .select(PROPERTY_SELECT)
            .order("created_at", { ascending: false })
            .range(from, from + pageSize - 1);
          if (propsError) throw propsError;
          if (data?.length) {
            allProperties.push(...(data as unknown as PropertyRow[]));
            hasMore = data.length === pageSize;
            from += pageSize;
          } else hasMore = false;
        }
        from = 0;
        hasMore = true;
        while (hasMore) {
          const { data, error: projectsError } = await db
            .from("projects")
            .select("id, name, description, created_at, created_by, status, client_code, logo_url")
            .order("name", { ascending: true })
            .range(from, from + pageSize - 1);
          if (projectsError) throw projectsError;
          if (data?.length) {
            allProjects.push(...data);
            hasMore = data.length === pageSize;
            from += pageSize;
          } else hasMore = false;
        }
      } else {
        // ── Client-portal path: single org via claim, properties by ref_id ───
        resolvedClientOrgId = await resolveClientId();
        const refIds = await fetchAuthorizedPropertyRefIds();
        if (refIds.length > 0 && resolvedClientOrgId) {
          for (const idChunk of chunkIds(refIds, CHUNK_SIZE)) {
            const { data, error: propsError } = await db
              .from("properties")
              .select(PROPERTY_SELECT)
              // Pin to the caller's org: the ref_id set can span orgs (unsynced
              // duplicate rows share a ref_id), so ref_id alone over-counts.
              .in("ref_id", idChunk)
              .eq("organization_id", resolvedClientOrgId);
            if (propsError) throw propsError;
            if (data?.length) allProperties.push(...(data as unknown as PropertyRow[]));
          }
        }
        if (resolvedClientOrgId) {
          const { data, error: projectsError } = await db
            .from("projects")
            .select("id, name, description, created_at, created_by, status, client_code, logo_url")
            .eq("id", resolvedClientOrgId)
            .maybeSingle();
          if (projectsError) throw projectsError;
          if (data) allProjects = [data];
        }
      }

      // Asset counts per project id (or 'unassigned'), summed from the per-property
      // server-side counts embedded above. Same shape as before — grouped by the
      // property's project/org — just without the full property_assets scan.
      const assetCounts: Record<string, number> = {};
      for (const p of allProperties) {
        const projId =
          (resolvedClientOrgId ?? p.organization_id ?? p.project_id) || "unassigned";
        const count = p.property_assets?.[0]?.count ?? 0;
        assetCounts[projId] = (assetCounts[projId] || 0) + count;
      }

      // PropertyRow is a structural DbProperty (the embedded count is extra), so it
      // assigns cleanly; downstream consumers only read the declared columns.
      const properties: DbProperty[] = allProperties;

      return { properties, dbProjects: allProjects, clientOrgId: resolvedClientOrgId, assetCounts };
    },
  });

  const properties = data?.properties ?? EMPTY_PROPERTIES;
  const dbProjects = data?.dbProjects ?? EMPTY_PROJECTS;
  const clientOrgId = data?.clientOrgId ?? null;
  const assetCounts = data?.assetCounts ?? EMPTY_COUNTS;
  // No user → query is disabled and never resolves; report not-loading (matches the
  // prior hook, which returned early without flipping the loading flag).
  const loading = !!user && isPending;
  const error = isError
    ? (queryError as Error)?.message ?? "Failed to load projects and properties"
    : null;

  // Force a fresh fetch (used after mutations below); bypasses staleTime.
  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Surface load failures — TanStack Query v5 removed the onError callback.
  useEffect(() => {
    if (!isError) return;
    console.error("[useProjects] Error fetching:", queryError);
    toast({
      title: "Error loading projects",
      description: (queryError as Error)?.message ?? "Failed to load projects and properties",
      variant: "destructive",
    });
  }, [isError, queryError, toast]);

  // One-time seed for demo client "7-Eleven" for this user
  const ensureSeed = useCallback(async () => {
    if (!user) return;
    if (seededRef.current) return;

    const supabaseUserId = requireUserIdForDatabase();
    const seedKey = `ri-seeded-7eleven-${supabaseUserId}`;
    if (localStorage.getItem(seedKey)) {
      seededRef.current = true;
      return;
    }

    try {
      const { data: existing, error: findError } = await db
        .from("projects")
        .select("id")
        .eq("created_by", supabaseUserId)
        .eq("name", "7-Eleven")
        .maybeSingle();

      if (findError && findError.code !== "PGRST116") {
        // PGRST116: No rows
        console.warn("[useProjects] Seed check warning:", findError.message);
      }

      if (!existing) {
        // console.log("[useProjects] Seeding demo project: 7-Eleven");
        // const { data: inserted, error: insertError } = await db
        //   .from("projects")
        //   .insert({
        //     name: "7-Eleven",
        //     description: "Sample client for demo",
        //     created_by: user.id,
        //     client_code: "7ELEVEN",
        //   })
        //   .select("id")
        //   .single();
        // if (insertError) {
        //   console.warn("[useProjects] Failed to seed 7-Eleven:", insertError.message);
        // } else {
        //   console.log("[useProjects] Seeded 7-Eleven project with id", inserted?.id);
        //   localStorage.setItem(seedKey, "1");
        //   seededRef.current = true;
        //   // Refresh projects list to include the seeded one
        //   await refetch();
        // }
      } else {
        localStorage.setItem(seedKey, "1");
        seededRef.current = true;
      }
    } catch (e) {
      console.warn("[useProjects] Seed error:", e);
    }
  }, [user]);

  useEffect(() => {
    // Try to seed once after initial fetch
    ensureSeed();
  }, [ensureSeed, dbProjects.length]);

  // Build derived list: Projects with their properties, plus virtual "Unassigned"
  const projects: ProjectWithProperties[] = useMemo(() => {
    // Map Supabase projects -> adapter Project type
    const mapped: Record<string, ProjectWithProperties> = {};
    dbProjects.forEach((p) => {
      const proj: Project = {
        id: p.id,
        name: p.name,
        description: p.description || undefined,
        created_at: p.created_at,
        logo_url: (p as any).logo_url ?? undefined,
      };
      mapped[p.id] = {
        ...proj,
        properties: [],
        assetsCount: assetCounts[p.id] ?? 0,
      };
    });

    const unassigned: ProjectWithProperties = {
      id: "unassigned",
      name: "Unassigned",
      description: "Properties not yet assigned to a project",
      created_at: "",
      properties: [],
      assetsCount: assetCounts["unassigned"] ?? 0,
    };

    properties.forEach((prop) => {
      const compactProp = {
        id: prop.id,
        name: prop.name,
        address: prop.address ?? undefined,
        city: prop.city ?? undefined,
        state: prop.state ?? undefined,
        country: prop.country ?? undefined,
        property_type: prop.property_type ?? undefined,
        latitude: prop.latitude ?? undefined,
        longitude: prop.longitude ?? undefined,
        delivery_status: (prop.delivery_status as "in_progress" | "delivered") ?? undefined,
        updated_at: prop.updated_at ?? undefined,
      };

      // Client-portal: every authorized property belongs to the single claim org.
      // Admin (clientOrgId null): group by each property's own org.
      const pid = (clientOrgId ?? prop.organization_id ?? prop.project_id) || undefined;
      if (pid && mapped[pid]) {
        mapped[pid].properties.push(compactProp);
      } else {
        unassigned.properties.push(compactProp);
      }
    });

    const list = Object.values(mapped).sort((a, b) => a.name.localeCompare(b.name));
    if (unassigned.properties.length > 0) list.unshift(unassigned);
    return list;
  }, [dbProjects, properties, assetCounts, clientOrgId]);

  // Raw list of DB projects in adapter shape (for compat)
  const rawProjects: Project[] = useMemo(() => {
    return dbProjects.map((p) => ({
      id: p.id,
      name: p.name,
      description: p.description || undefined,
      created_at: p.created_at,
    }));
  }, [dbProjects]);

  // For compat with previous API (not used by UI now)
  const projectMap = useMemo(() => {
    const map: Record<string, string> = {};
    properties.forEach((p) => {
      if (p.project_id) map[p.id] = p.project_id;
    });
    return map;
  }, [properties]);

  // Actions: all persist to Supabase
  const actions = {
    createProject: async (name: string, description?: string) => {
      if (!user) return null;
      const supabaseUserId = requireUserIdForDatabase();
      const { data, error } = await db
        .from("projects")
        .insert({
          name,
          description: description || null,
          created_by: supabaseUserId,
        })
        .select("id, name, description, created_at, created_by")
        .single();

      if (error) {
        console.error("[useProjects] createProject error:", error);
        toast({
          title: "Could not create project",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }

      toast({ title: "Project created", description: `“${name}” has been created.` });
      refresh();
      const p: Project = {
        id: data.id,
        name: data.name,
        description: data.description || undefined,
        created_at: data.created_at,
      };
      return p;
    },

    assign: async (projectId: string, propertyIds: string[]) => {
      if (!user) return;
      if (!propertyIds?.length) return;
      if (projectId === "unassigned") {
        toast({
          title: "Cannot assign to Unassigned",
          description: "Please assign to a real project.",
          variant: "destructive",
        });
        return;
      }

      const supabaseUserId = requireUserIdForDatabase();
      const { error } = await db
        .from("properties")
        .update({ organization_id: projectId })
        .in("id", propertyIds)
        .eq("created_by", supabaseUserId);

      if (error) {
        console.error("[useProjects] assign error:", error);
        toast({
          title: "Assignment failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({ title: "Properties assigned", description: `${propertyIds.length} updated.` });
      refresh();
    },

    unassign: async (propertyIds: string[]) => {
      if (!user) return;
      if (!propertyIds?.length) return;

      const supabaseUserId = requireUserIdForDatabase();
      // Unassign must null BOTH columns. Nulling organization_id alone would let
      // the sync worker re-populate it from the lingering project_id, reverting
      // the unassign. project_id is dropped here only until Phase 6 cleanup.
      const { error } = await db
        .from("properties")
        .update({ organization_id: null, project_id: null } as any)
        .in("id", propertyIds)
        .eq("created_by", supabaseUserId);

      if (error) {
        console.error("[useProjects] unassign error:", error);
        toast({
          title: "Unassign failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({ title: "Properties unassigned", description: `${propertyIds.length} updated.` });
      refresh();
    },

    rename: async (id: string, name: string) => {
      if (!user) return;

      const supabaseUserId = requireUserIdForDatabase();
      const { error } = await db.from("projects").update({ name }).eq("id", id).eq("created_by", supabaseUserId);

      if (error) {
        console.error("[useProjects] rename error:", error);
        toast({
          title: "Rename failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({ title: "Project renamed", description: `Renamed to “${name}”.` });
      refresh();
    },

    remove: async (id: string) => {
      if (!user) return;

      const supabaseUserId = requireUserIdForDatabase();
      const { error } = await db.from("projects").delete().eq("id", id).eq("created_by", supabaseUserId);

      if (error) {
        console.error("[useProjects] delete error:", error);
        toast({
          title: "Delete failed",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      // properties.project_id will be set to NULL due to FK ON DELETE SET NULL
      toast({ title: "Project deleted", description: "Properties were left unassigned." });
      refresh();
    },

    reload: () => refetch(),
  };

  return {
    loading,
    error,
    projects,
    rawProjects,
    projectMap,
    properties,
    ...actions,
  };
}
